import type { CaseFact, CaseImage, CaseSource, CaseStat } from "./caseStudies";
import {
  coverBoomid,
  coverRiskrator,
  coverEnapro,
  coverCrucijuegos,
  coverSanCristobal,
  coverPrevinca,
  coverVerona,
  coverSonder,
} from "@/assets";

/**
 * Perfil publico de cada cliente de los casos de exito.
 *
 * Qué es y de dónde sale: `about` y `facts` son datos PUBLICOS del cliente
 * (prensa, su sitio, registros), no resultados de WAIA, y cada caso lista sus
 * `sources` para que se puedan verificar. Lo que no se pudo confirmar se dejó
 * afuera o se dice con el hedge de la fuente ("según Forbes Argentina").
 *
 * `stats` y `tech` solo rellenan lo que el caso no traía y salen de lo que el
 * propio texto del caso ya cuenta (alcance, canales, tecnologias), nunca de
 * cifras nuevas: un numero de resultado que WAIA no publico no se inventa.
 * `year` y `tech` del caso original, si existen, mandan sobre estos.
 *
 * Se aplican en caseStudies.ts (applyProfile).
 */

interface ProfileContent {
  /** Rubro del cliente, en una frase corta. */
  sector: string;
  /** Quien es el cliente, en dos o tres lineas. */
  about: string;
  /** Datos duros del cliente (no de WAIA). */
  facts: CaseFact[];
  /** Solo se usa si el caso no tiene `stats` propios. */
  stats?: CaseStat[];
  /** Solo se usa si el caso no tiene `tech` propio. */
  tech?: string[];
}

export interface CaseProfile extends ProfileContent {
  /** Portada para los casos sin capturas propias. Siempre con su credito: son
   * imagenes de internet (el sitio del cliente o dominio publico), no de WAIA. */
  cover?: CaseImage;
  /** Solo si esta confirmado: un dato de ubicacion dudoso es peor que ninguno. */
  location?: string;
  sources: CaseSource[];
  en: ProfileContent;
}

export const caseProfiles: Record<string, CaseProfile> = {
  elgrafico: {
    location: "Buenos Aires, Argentina",
    sector: "Medios deportivos",
    about:
      "Revista deportiva argentina fundada en 1919 por Constancio Vigil. Se volcó de lleno al deporte en 1925, salió semanalmente hasta 2002 y hoy publica una edición mensual y un sitio de noticias.",
    facts: [
      { value: "1919", label: "primer número, el 30 de mayo" },
      { value: "1925", label: "se vuelca al deporte" },
      { value: "107 años", label: "de historia, según su propio sitio (2026)" },
    ],
    stats: [
      { value: "0 h", label: "de trabajo manual del equipo editorial por nota" },
      { value: "Voz real", label: "de cada periodista, con clonación de voz" },
      { value: "CDN", label: "carga asíncrona: sin costo en velocidad de página" },
    ],
    sources: [
      { label: "El Gráfico: el primer número", url: "https://elgrafico.com.ar/articulo/1098/33904/este-fue-el-primer-numero-de-el-grafico" },
      { label: "El Gráfico: su génesis", url: "https://elgrafico.com.ar/articulo/%C2%A1habla-memoria!/67550/el-grafico-y-su-genesis-la-primera-tapa-de-futbol-de-la-revista" },
    ],
    en: {
      sector: "Sports media",
      about:
        "Argentine sports magazine founded in 1919 by Constancio Vigil. It turned fully to sport in 1925, ran weekly until 2002 and today publishes a monthly edition and a news site.",
      facts: [
        { value: "1919", label: "first issue, on May 30" },
        { value: "1925", label: "turns to sport" },
        { value: "107 years", label: "of history, per its own site (2026)" },
      ],
      stats: [
        { value: "0 h", label: "of manual work for the editorial team per article" },
        { value: "Real voice", label: "of each journalist, with voice cloning" },
        { value: "CDN", label: "asynchronous loading: no cost in page speed" },
      ],
    },
  },

  sancristobal: {
    cover: {
      src: coverSanCristobal,
      fill: true,
      alt: "Celular con SANTI, el asistente digital de San Cristóbal Seguros, atendiendo por WhatsApp",
      credit: "Imagen: sitio web de San Cristóbal Seguros",
      creditUrl: "https://www.sancristobal.com.ar",
    },
    location: "Rosario, Santa Fe, Argentina",
    sector: "Seguros",
    about:
      "Grupo asegurador argentino que nació de la mutual San Cristóbal, fundada en 1939 en Rosario. Reúne San Cristóbal Seguros (Argentina y Uruguay), Asociart ART, iúnigo y San Cristóbal Retiro.",
    facts: [
      { value: "1939", label: "fundada en Rosario" },
      { value: "+1.900", label: "empleados, según Forbes Argentina" },
      { value: "2 países", label: "Argentina y Uruguay" },
    ],
    stats: [
      { value: "1 año", label: "sosteniendo el nivel técnico exigido" },
      { value: "2 países", label: "Argentina y Uruguay, con sitios propios" },
    ],
    tech: ["Equipo dedicado", "Desarrollo web", "Sitios orientados a conversión"],
    sources: [
      { label: "Forbes Argentina: Diego Guaita, CEO del grupo", url: "https://www.forbesargentina.com/liderazgo/diego-guaita-ceo-grupo-san-cristobal-la-agenda-coyuntural-le-saca-tiempo-largo-plazo-n34987" },
      { label: "Infonegocios: San Cristóbal Seguros", url: "https://infonegocios.info/tarjetero/empresa/san-cristobal-seguros" },
    ],
    en: {
      sector: "Insurance",
      about:
        "Argentine insurance group that grew out of the San Cristóbal mutual, founded in 1939 in Rosario. It brings together San Cristóbal Seguros (Argentina and Uruguay), Asociart ART, iúnigo and San Cristóbal Retiro.",
      facts: [
        { value: "1939", label: "founded in Rosario" },
        { value: "1,900+", label: "employees, per Forbes Argentina" },
        { value: "2 countries", label: "Argentina and Uruguay" },
      ],
      stats: [
        { value: "1 year", label: "holding the demanding technical level" },
        { value: "2 countries", label: "Argentina and Uruguay, with their own sites" },
      ],
      tech: ["Dedicated team", "Web development", "Conversion-focused sites"],
    },
  },

  crucijuegos: {
    cover: {
      src: coverCrucijuegos,
      fill: true,
      alt: "Plaza de juegos de noche con un tobogán rosa y una figura de colores, obra de Crucijuegos",
      credit: "Imagen: sitio web de Bongo (marca de Crucijuegos)",
      creditUrl: "https://www.bongo.com.ar",
    },
    location: "Rosario, Santa Fe, Argentina",
    sector: "Juegos de plaza y mobiliario urbano",
    about:
      "Empresa rosarina fundada en 1992 que diseña y fabrica juegos de plaza, mobiliario urbano y espacios recreativos para ámbitos públicos y privados, con un equipo de diseño industrial y ventas en las 23 provincias.",
    facts: [
      { value: "1992", label: "fundada en Rosario" },
      { value: "+1 millón", label: "de espacios para las infancias, según Forbes Argentina" },
      { value: "23", label: "provincias donde vende" },
    ],
    sources: [
      { label: "Forbes Argentina: la historia de Crucijuegos", url: "https://www.forbesargentina.com/negocios/crucijuegos-historia-empresa-rosarina-creo-mas-1-millon-espacios-infancias-cientos-plazas-argentinas-n88869" },
      { label: "La Capital: el diseño rosarino de exportación", url: "https://www.lacapital.com.ar/economia/crucijuegos-el-diseno-rosarino-exportacion-n10026310.html" },
    ],
    en: {
      sector: "Playgrounds and street furniture",
      about:
        "Rosario company founded in 1992 that designs and manufactures playground equipment, street furniture and recreational spaces for public and private settings, with an industrial design team and sales in all 23 provinces.",
      facts: [
        { value: "1992", label: "founded in Rosario" },
        { value: "1M+", label: "children's spaces, per Forbes Argentina" },
        { value: "23", label: "provinces where it sells" },
      ],
    },
  },

  sonder: {
    cover: {
      src: coverSonder,
      alt: "Tres remeras de Sonder: una estampada oscura, una lisa gris y una musculosa blanca",
      credit: "Imagen: tienda online de Sonder",
      creditUrl: "https://www.sonder.com.ar",
      fill: true,
    },
    location: "Rosario, Santa Fe, Argentina",
    sector: "Indumentaria deportiva",
    about:
      "Marca rosarina de indumentaria deportiva con locales propios, tienda online y venta en más de 300 casas de deportes y locales multimarca de todo el país. Patrocina instituciones, deportistas y seleccionados.",
    facts: [
      { value: "+300", label: "casas de deportes y multimarcas donde vende" },
      { value: "Online", label: "tienda propia en sonder.com.ar" },
      { value: "+27 años", label: "de trayectoria, según la prensa del sector" },
    ],
    tech: ["Integraciones en tiempo real", "Probador virtual con IA", "E-commerce mayorista y minorista"],
    sources: [
      { label: "Modaes: Sonder continúa su expansión", url: "https://www.modaes.com/empresa/sonder-continua-con-su-expansion-en-argentina-abrira-tiendas-en-salta-y-mendoza" },
      { label: "América Retail: una marca rosarina de indumentaria deportiva", url: "https://americaretail-malls.com/malls/argentina-malls/llega-a-paso-del-bosque-una-marca-rosarina-de-indumentaria-deportiva/" },
    ],
    en: {
      sector: "Sportswear",
      about:
        "Rosario sportswear brand with its own stores, an online shop and sales through more than 300 sports stores and multi-brand retailers across the country. It sponsors institutions, athletes and national teams.",
      facts: [
        { value: "300+", label: "sports stores and multi-brand retailers" },
        { value: "Online", label: "own shop at sonder.com.ar" },
        { value: "27+ years", label: "in business, per trade press" },
      ],
      tech: ["Real-time integrations", "AI virtual try-on", "Wholesale and retail e-commerce"],
    },
  },

  enapro: {
    cover: {
      src: coverEnapro,
      fill: true,
      alt: "Fotografía histórica del Puerto de Rosario con grúas y barcos",
      credit: "Archivo General de la Nación, dominio público, vía Wikimedia Commons",
      creditUrl: "https://commons.wikimedia.org/wiki/File:Puerto_de_Rosario,_Argentina_(AGN).jpg",
    },
    location: "Rosario, Santa Fe, Argentina",
    sector: "Puertos y logística",
    about:
      "Ente público creado en 1993 por la Provincia de Santa Fe. Administra el Puerto de Rosario desde 1994 y otorga concesiones a terminales privadas sobre el río Paraná.",
    facts: [
      { value: "1993", label: "creado por la Provincia de Santa Fe" },
      { value: "km 395–420", label: "tramo del Paraná bajo su jurisdicción" },
      { value: "30 años", label: "de concesión de la terminal multipropósito, desde 2002" },
    ],
    stats: [
      { value: "3 públicos", label: "empresas, visitantes y ciudadanía, en una misma experiencia" },
      { value: "IA", label: "conversacional dentro del sitio institucional" },
    ],
    tech: ["IA conversacional", "Experiencia interactiva web"],
    sources: [
      { label: "Wikipedia: Port of Rosario", url: "https://en.wikipedia.org/wiki/Port_of_Rosario" },
      { label: "Aduana News: balance económico de ENAPRO", url: "https://aduananews.com/en/enapro-positivo-balance-economico-en-puerto-rosario/" },
    ],
    en: {
      sector: "Ports and logistics",
      about:
        "Public body created in 1993 by the Province of Santa Fe. It has run the Port of Rosario since 1994 and grants concessions to private terminals on the Paraná river.",
      facts: [
        { value: "1993", label: "created by the Province of Santa Fe" },
        { value: "km 395–420", label: "stretch of the Paraná under its jurisdiction" },
        { value: "30 years", label: "concession of the multipurpose terminal, since 2002" },
      ],
      stats: [
        { value: "3 audiences", label: "companies, visitors and citizens, in one experience" },
        { value: "AI", label: "conversational layer inside the institutional site" },
      ],
      tech: ["Conversational AI", "Interactive web experience"],
    },
  },

  riskrator: {
    cover: {
      src: coverRiskrator,
      fill: true,
      alt: "Flujo de datos y código binario, imagen del sitio de RiskRator",
      credit: "Imagen: sitio web de RiskRator",
      creditUrl: "https://riskrator.com",
    },
    sector: "Cumplimiento y riesgo financiero",
    about:
      "Plataforma de evaluación de riesgo BSA/AML y OFAC basada en transacciones, pensada para bancos y cooperativas de crédito (credit unions).",
    facts: [
      { value: "BSA/AML", label: "evaluación de riesgo de lavado de dinero" },
      { value: "OFAC", label: "cumplimiento de sanciones" },
      { value: "Bancos", label: "y credit unions, su segmento" },
    ],
    tech: ["Automatización de procesos", "Trazabilidad y control", "Integración con bancos"],
    sources: [{ label: "RiskRator en LinkedIn", url: "https://linkedin.com/company/riskrator" }],
    en: {
      sector: "Compliance and financial risk",
      about:
        "Transaction-based BSA/AML and OFAC risk assessment platform built for banks and credit unions.",
      facts: [
        { value: "BSA/AML", label: "money-laundering risk assessment" },
        { value: "OFAC", label: "sanctions compliance" },
        { value: "Banks", label: "and credit unions, its segment" },
      ],
      tech: ["Process automation", "Traceability and control", "Bank integrations"],
    },
  },

  previnca: {
    cover: {
      src: coverPrevinca,
      alt: "María Pía, la asistente virtual de Previnca Salud, con auriculares y micrófono",
      fill: true,
    },
    location: "San Lorenzo, Santa Fe, Argentina",
    sector: "Salud",
    about:
      "Previnca Salud S.A., sociedad constituida en 2003 con sede en San Lorenzo, Santa Fe. Atiende a sus afiliados con turnos, consultas e información administrativa.",
    facts: [
      { value: "2003", label: "sociedad constituida" },
      { value: "San Lorenzo", label: "Santa Fe, su sede" },
      { value: "24 h", label: "de atención digital con su asistente de IA" },
    ],
    sources: [{ label: "Registro de sociedades: Previnca Salud S.A.", url: "https://www.indicadores.ar/empresa/30689165695" }],
    en: {
      sector: "Healthcare",
      about:
        "Previnca Salud S.A., a company incorporated in 2003 and based in San Lorenzo, Santa Fe. It serves its members with appointments, queries and administrative information.",
      facts: [
        { value: "2003", label: "company incorporated" },
        { value: "San Lorenzo", label: "Santa Fe, its base" },
        { value: "24 h", label: "of digital service with its AI assistant" },
      ],
    },
  },

  boomid: {
    cover: {
      src: coverBoomid,
      fill: true,
      alt: "Persona en el acceso de un edificio, imagen del sitio de BoomID sobre control de visitantes",
      credit: "Imagen: sitio web de BoomID",
      creditUrl: "https://boomid.io",
    },
    sector: "Identidad y biometría",
    about:
      "Plataforma de autorización de identidad con motor de riesgo propio, fundada en 2020. Combina biometría de palma sin contacto (alianza con Innovatrics) con puntaje de riesgo en tiempo real y control de cumplimiento continuo.",
    facts: [
      { value: "2020", label: "fundada" },
      { value: "Palma", label: "biometría sin contacto, con Innovatrics" },
      { value: "Tiempo real", label: "puntaje de riesgo y cumplimiento continuo" },
    ],
    stats: [
      { value: "99,9%", label: "de disponibilidad validada en producción" },
      { value: "2 a 10", label: "instancias por servicio con auto-escalado" },
      { value: "Multi-AZ", label: "redundancia entre zonas de disponibilidad" },
    ],
    sources: [
      { label: "Biometric Update: Boom ID y Innovatrics", url: "https://www.biometricupdate.com/202607/boom-id-integrates-innovatrics-palm-biometrics-for-mobile-enterprise-authorization" },
      { label: "Innovatrics: alianza con Boom ID", url: "https://www.innovatrics.com/news/innovatrics-partners-with-boom-id-to-bring-contactless-palm-recognition-to-identity-authorization/" },
      { label: "Village: perfil de BoomID", url: "https://village.ai/company/boomid" },
    ],
    en: {
      sector: "Identity and biometrics",
      about:
        "Identity authorization platform with its own risk engine, founded in 2020. It combines contactless palm biometrics (partnership with Innovatrics) with real-time risk scoring and continuous compliance monitoring.",
      facts: [
        { value: "2020", label: "founded" },
        { value: "Palm", label: "contactless biometrics, with Innovatrics" },
        { value: "Real time", label: "risk scoring and continuous compliance" },
      ],
      stats: [
        { value: "99.9%", label: "availability validated in production" },
        { value: "2 to 10", label: "instances per service with auto-scaling" },
        { value: "Multi-AZ", label: "redundancy across availability zones" },
      ],
    },
  },

  verona: {
    cover: {
      src: coverVerona,
      alt: "Logo de Neumáticos Verona sobre tres neumáticos Pirelli: Scorpion ATR, Cinturato P1 y Cinturato P7",
      credit: "Imagen: tienda de Neumáticos Verona en Mercado Libre",
      creditUrl: "https://www.mercadolibre.com.ar/tienda/neumaticos-verona",
      fill: true,
    },
    location: "Argentina",
    sector: "Neumáticos y autopartes",
    about:
      "Comercio de neumáticos de distintas marcas con venta online, tanto en su propia tienda como en Mercado Libre, y atención en sucursal.",
    facts: [
      { value: "Online", label: "tienda propia y Mercado Libre" },
      { value: "Sucursal", label: "atención con turnos y tótems interactivos" },
    ],
    tech: ["E-commerce", "Tótems interactivos", "Bot de WhatsApp"],
    sources: [{ label: "Neumáticos Verona en Mercado Libre", url: "https://www.mercadolibre.com.ar/tienda/neumaticos-verona" }],
    en: {
      sector: "Tyres and auto parts",
      about:
        "Tyre retailer selling several brands online, both through its own shop and Mercado Libre, with in-branch service.",
      facts: [
        { value: "Online", label: "own shop and Mercado Libre" },
        { value: "Branch", label: "appointments and interactive kiosks" },
      ],
      tech: ["E-commerce", "Interactive kiosks", "WhatsApp bot"],
    },
  },

  emerger: {
    location: "Rosario, Santa Fe, Argentina",
    sector: "Salud prehospitalaria",
    about:
      "Empresa de emergencias médicas con sede en Rosario y cobertura en todo el país. En 2025 cumplió 31 años y abrió una nueva base operativa en la Ciudad de Buenos Aires.",
    facts: [
      { value: "31 años", label: "de historia (2025)" },
      { value: "Todo el país", label: "cobertura nacional" },
      { value: "2025", label: "nueva base operativa en Buenos Aires" },
    ],
    stats: [
      { value: "2 canales", label: "descomprimidos: la sala de atención y el teléfono" },
      { value: "Hoy", label: "la app sigue en funcionamiento" },
    ],
    sources: [
      { label: "La Capital: Emerger se expande en Buenos Aires", url: "https://www.lacapital.com.ar/salud/emerger-continua-expandiendose-buenos-aires-n10203975.html" },
      { label: "La Capital: una empresa que sigue innovando en salud", url: "https://www.lacapital.com.ar/novedades/emerger-una-empresa-que-sigue-creciendo-e-innovando-salud-n10222749.html" },
    ],
    en: {
      sector: "Pre-hospital care",
      about:
        "Medical emergency company based in Rosario with nationwide coverage. In 2025 it turned 31 and opened a new operations base in Buenos Aires.",
      facts: [
        { value: "31 years", label: "in business (2025)" },
        { value: "Nationwide", label: "coverage" },
        { value: "2025", label: "new operations base in Buenos Aires" },
      ],
      stats: [
        { value: "2 channels", label: "relieved: the service room and the phone line" },
        { value: "Today", label: "the app is still running" },
      ],
    },
  },

  marriott: {
    location: "Bethesda, Estados Unidos",
    sector: "Hotelería",
    about:
      "Marriott International es una de las mayores cadenas hoteleras del mundo. Su programa de fidelización, Marriott Bonvoy, reúne 31 marcas y más de 270 millones de miembros.",
    facts: [
      { value: "+270 M", label: "de miembros en Marriott Bonvoy (2025)" },
      { value: "31", label: "marcas hoteleras" },
      { value: "+8.600", label: "propiedades en 138 países y regiones" },
    ],
    stats: [
      { value: "Caribe y LatAm", label: "zonas donde se potenció la captación" },
      { value: "Landing pages", label: "integradas a sus sistemas" },
    ],
    tech: ["Landing pages", "Integración con sistemas del cliente"],
    sources: [
      { label: "Forbes España: resultados de Marriott en 2025", url: "https://forbes.es/forbestravel/871803/marriott-gana-2-601-millones-de-dolares-en-2025-10-y-eleva-sus-ingresos-un-4-hasta-los-26-186-millones/" },
      { label: "ANA: Marriott Bonvoy", url: "https://www.ana.co.jp/es/mx/amc/partner-hotels/marriott-bonvoy/" },
    ],
    en: {
      sector: "Hospitality",
      about:
        "Marriott International is one of the largest hotel companies in the world. Its loyalty program, Marriott Bonvoy, spans 31 brands and more than 270 million members.",
      facts: [
        { value: "270M+", label: "Marriott Bonvoy members (2025)" },
        { value: "31", label: "hotel brands" },
        { value: "8,600+", label: "properties in 138 countries and regions" },
      ],
      stats: [
        { value: "Caribbean and LatAm", label: "regions where sign-ups were boosted" },
        { value: "Landing pages", label: "integrated with their systems" },
      ],
      tech: ["Landing pages", "Integration with the client's systems"],
    },
  },
};
